/* ==========================================================================
   Rodeo Soul Harmony — page behaviour
   Ported from the retired Hydrogen route. Loaded as a module, so it is
   evaluated once no matter how many RSH sections request it.
   ========================================================================== */

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Runs `fn` against an element at most once, so re-boots after a theme-editor
    section reload don't stack duplicate listeners on surviving nodes. */
function once(element, flag, fn) {
  if (element.dataset[flag] === 'true') return;
  element.dataset[flag] = 'true';
  fn(element);
}
const MOBILE = () => window.matchMedia('(max-width: 767px)').matches;

/* --------------------------------------------------------------------------
   Lenis smooth scroll — identical options to the Hydrogen build (lenis 1.3.23)
   -------------------------------------------------------------------------- */
function initLenis() {
  if (window.__lenis) return;

  if (REDUCED_MOTION || !window.Lenis) {
    // No smooth scroll: the native scroll event is all we need.
    window.addEventListener('scroll', notifyScroll, { passive: true });
    return;
  }

  const lenis = new window.Lenis({
    duration: 1.4,
    easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    smoothWheel: true,
    wheelMultiplier: 1,
    touchMultiplier: 2,
    lerp: 0.08,
  });

  lenis.scrollTo(0, { immediate: true });
  document.documentElement.classList.add('lenis', 'lenis-smooth');
  window.__lenis = lenis;

  const raf = (time) => {
    lenis.raf(time);
    notifyScroll();
    requestAnimationFrame(raf);
  };
  requestAnimationFrame(raf);

  // The theme locks scrolling by setting html[scroll-lock] (cart drawer, menu
  // drawer, dialogs). Lenis doesn't watch attributes, so mirror it explicitly
  // or the page keeps scrolling behind an open drawer.
  const syncScrollLock = () => {
    if (document.documentElement.hasAttribute('scroll-lock')) lenis.stop();
    else lenis.start();
  };
  new MutationObserver(syncScrollLock).observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['scroll-lock'],
  });
  syncScrollLock();
}

/* In-page anchors get Lenis' own eased scroll, with the distance-based
   duration ramp the source page used. */
function initAnchors() {
  document.addEventListener('click', (event) => {
    const scope = event.target instanceof Element ? event.target.closest('.rsh-scope') : null;
    if (!scope) return;

    const link = event.target.closest('a[href^="#"]');
    if (!link) return;

    const href = link.getAttribute('href');
    if (!href || href.length < 2) return;

    // Anchors normally target an id. One exception: the Okendo reviews
    // widget lives in a generic theme `_blocks` section we don't own the
    // markup of, so it can only be tagged via that section's `custom_class`
    // setting rather than a real id — fall back to reading the hash as a
    // class name in that case.
    const target = document.querySelector(href) || document.querySelector(href.replace(/^#/, '.'));
    if (!target) return;

    event.preventDefault();

    const lenis = window.__lenis;
    if (!lenis) {
      target.scrollIntoView({ block: 'start', behavior: 'smooth' });
      return;
    }

    const distance = Math.abs(target.getBoundingClientRect().top);
    const duration = Math.min(3, Math.max(1.2, distance / 900));
    const padding = parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0;

    lenis.scrollTo(target, {
      offset: padding - 20,
      duration,
      easing: (t) => 1 - Math.pow(1 - t, 3),
    });
  });
}


/* Lenis drives scrolling itself and swallows the native `scroll` event, so
   position-dependent handlers can't rely on it. Instead they subscribe here and
   get notified from Lenis' own frame loop (see initLenis), falling back to the
   native scroll event when Lenis isn't running. No extra rAF loop either way. */
const scrollSubscribers = new Set();
let lastScrollY = -1;

function notifyScroll() {
  const y = window.scrollY;
  if (y === lastScrollY) return;
  lastScrollY = y;
  for (const fn of scrollSubscribers) fn(y);
}

/** Subscribes `callback` to scroll-position changes and viewport resizes. */
function onScrollTick(callback) {
  scrollSubscribers.add(callback);

  window.addEventListener(
    'resize',
    () => {
      lastScrollY = -1;
      callback();
    },
    { passive: true }
  );

  callback();
}

/* --------------------------------------------------------------------------
   Scroll reveals
   -------------------------------------------------------------------------- */
function initReveals() {
  const nodes = Array.from(
    document.querySelectorAll(
      '.rsh-scope .rsh-reveal, .rsh-scope .rsh-reveal-stagger, .rsh-scope .rsh-word-reveal'
    )
  ).filter((node) => !node.classList.contains('rsh-visible'));
  if (!nodes.length) return;

  if (REDUCED_MOTION) {
    nodes.forEach((node) => node.classList.add('rsh-visible'));
    return;
  }

  const reveal = (entries, observer) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('rsh-visible');
      observer.unobserve(entry.target);
    });
  };

  const standard = new IntersectionObserver(reveal, {
    threshold: 0.14,
    rootMargin: '0px 0px -8% 0px',
  });

  // The reviews grid is wider than the viewport once it becomes a carousel,
  // so it gets a looser threshold (as in the source).
  const wide = new IntersectionObserver(reveal, {
    threshold: 0.05,
    rootMargin: '0px 0px -2% 0px',
  });

  nodes.forEach((node) => {
    if (node.classList.contains('rsh-testi-grid')) wide.observe(node);
    else standard.observe(node);
  });
}

/* --------------------------------------------------------------------------
   Hero — parallax, sound toggle
   -------------------------------------------------------------------------- */
function initHero(hero) {
  const video = hero.querySelector('[data-rsh-hero-video]');
  const muteBtn = hero.querySelector('[data-rsh-mute]');

  // Some browsers refuse the initial autoplay promise; retry muted.
  if (video) {
    const play = () => video.play().catch(() => {});
    play();
    video.addEventListener('canplay', play, { once: true });
  }

  if (muteBtn && video) {
    const iconMuted = muteBtn.querySelector('[data-rsh-icon-muted]');
    const iconSound = muteBtn.querySelector('[data-rsh-icon-sound]');
    let muted = true;

    muteBtn.addEventListener('click', () => {
      muted = !muted;
      video.muted = muted;
      if (!muted) video.play().catch(() => {});

      if (iconMuted) iconMuted.style.display = muted ? '' : 'none';
      if (iconSound) iconSound.style.display = muted ? 'none' : '';

      muteBtn.setAttribute('aria-label', muted ? 'Unmute video' : 'Mute video');
      muteBtn.classList.remove('rsh-toggled');
      void muteBtn.offsetWidth;
      muteBtn.classList.add('rsh-toggled');
      setTimeout(() => muteBtn.classList.remove('rsh-toggled'), 450);
    });
  }

  if (REDUCED_MOTION) return;

  const layer = hero.querySelector('.rsh-parallax-hero');
  if (!layer) return;

  onScrollTick(() => {
    const y = window.scrollY;
    if (y < window.innerHeight * 1.2) {
      layer.style.transform = `translate3d(0, ${y * 0.18}px, 0) scale(1.04)`;
    }
  });
}

/* --------------------------------------------------------------------------
   Product gallery carousel
   -------------------------------------------------------------------------- */
function initGallery(root) {
  const track = root.querySelector('[data-rsh-track]');
  if (!track) return;

  const slides = Array.from(track.children);
  const dots = Array.from(root.querySelectorAll('[data-rsh-dot]'));
  const thumbs = Array.from(root.querySelectorAll('[data-rsh-thumb]'));
  const counter = root.querySelector('[data-rsh-counter]');
  const prev = root.querySelector('[data-rsh-prev]');
  const next = root.querySelector('[data-rsh-next]');
  const total = slides.length;

  if (total <= 1) return;

  let index = 0;
  const pad = (n) => String(n).padStart(2, '0');

  const render = () => {
    track.style.transform = `translateX(-${index * 100}%)`;

    dots.forEach((dot, i) => {
      dot.classList.toggle('rsh-active', i === index);
      if (i === index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });

    thumbs.forEach((thumb, i) => thumb.classList.toggle('rsh-active', i === index));

    if (counter) counter.textContent = `${pad(index + 1)} / ${pad(total)}`;

    const activeThumb = thumbs[index];
    if (activeThumb && activeThumb.scrollIntoView) {
      activeThumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    }
  };

  const go = (i) => {
    index = (i + total) % total;
    render();
  };

  prev?.addEventListener('click', () => go(index - 1));
  next?.addEventListener('click', () => go(index + 1));
  dots.forEach((dot, i) => dot.addEventListener('click', () => go(i)));
  thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => go(i)));

  // Pointer drag, 50px commit threshold — same as the source page.
  let startX = null;
  let startY = null;
  let axis = null;

  track.addEventListener('pointerdown', (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    startX = event.clientX;
    startY = event.clientY;
    axis = null;
    track.classList.add('rsh-dragging');
    track.setPointerCapture?.(event.pointerId);
  });

  track.addEventListener('pointermove', (event) => {
    if (startX === null || startY === null) return;
    const dx = event.clientX - startX;
    const dy = event.clientY - startY;
    if (axis === null && (Math.abs(dx) > 10 || Math.abs(dy) > 10)) {
      axis = Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical';
    }
    if (axis === 'horizontal') event.preventDefault();
  });

  const endDrag = (event) => {
    if (startX === null) {
      track.classList.remove('rsh-dragging');
      return;
    }
    const dx = event.clientX - startX;
    startX = null;
    startY = null;
    track.classList.remove('rsh-dragging');
    track.releasePointerCapture?.(event.pointerId);
    if (axis === 'horizontal') {
      if (dx > 50) go(index - 1);
      else if (dx < -50) go(index + 1);
    }
  };

  track.addEventListener('pointerup', endDrag);
  track.addEventListener('pointercancel', endDrag);

  render();
}

/* --------------------------------------------------------------------------
   Truncated description ("Read more" only appears when it actually clips)
   -------------------------------------------------------------------------- */
function initDescription(root) {
  const wrap = root.querySelector('[data-rsh-desc]');
  if (!wrap) return;

  const inner = wrap.querySelector('.rsh-spotlight-desc-inner');
  const toggle = wrap.querySelector('[data-rsh-desc-toggle]');
  if (!inner || !toggle) return;

  const clampLines = () => (MOBILE() ? 3 : 2);

  const measure = () => {
    const lineHeight = parseFloat(getComputedStyle(inner).lineHeight) || 27.2;
    const overflows = inner.scrollHeight > lineHeight * clampLines() + 4;
    wrap.classList.toggle('rsh-has-toggle', overflows);
    toggle.hidden = !overflows;
  };

  measure();
  new ResizeObserver(measure).observe(inner);

  toggle.addEventListener('click', () => {
    const expanded = wrap.classList.toggle('rsh-expanded');
    toggle.setAttribute('aria-expanded', String(expanded));
    toggle.textContent = expanded ? toggle.dataset.less : toggle.dataset.more;
  });
}

/* --------------------------------------------------------------------------
   Mobile scroll-snap carousels (scent story + reviews) — dot indicators
   -------------------------------------------------------------------------- */
function initSnapDots(root, trackSelector, dotsSelector, gap) {
  const track = root.querySelector(trackSelector);
  const dotsWrap = root.querySelector(dotsSelector);
  if (!track || !dotsWrap) return;

  const dots = Array.from(dotsWrap.children);
  if (!dots.length) return;

  const setActive = (i) => dots.forEach((dot, n) => dot.classList.toggle('rsh-active', n === i));
  setActive(0);

  let queued = false;
  track.addEventListener(
    'scroll',
    () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        const first = track.children[0];
        if (first) {
          const step = first.offsetWidth + gap;
          setActive(Math.min(dots.length - 1, Math.round(track.scrollLeft / step)));
        }
        queued = false;
      });
    },
    { passive: true }
  );

  dots.forEach((dot, i) => {
    dot.addEventListener('click', () => {
      const first = track.children[0];
      if (!first) return;
      track.scrollTo({ left: i * (first.offsetWidth + gap), behavior: 'smooth' });
    });
  });
}


/* --------------------------------------------------------------------------
   Cart drawer bridge

   The theme's add-to-cart buttons (rsh-add-to-cart.liquid) go through the
   Horizon-style `product-form-component` / `add-to-cart-component` pair
   shipped in assets/product-form.js, which does the actual POST to
   /cart/add.js and dispatches a bubbling `cart:update` event on success.

   That's as far as those components take it, though: this storefront's
   *live* cart drawer is not `<cart-drawer-component>` (that markup is never
   rendered by sections/cart-drawer.liquid on this theme) — it's the legacy
   jQuery drawer in that same section, which opens and repaints itself only
   when its own global `refreshCartAndOpen()` is called. Without this
   bridge, our forms add the item successfully (confirmed by it appearing
   after a manual refresh) but nothing tells the actual drawer to open.
   -------------------------------------------------------------------------- */
function initCartDrawerBridge() {
  document.addEventListener('cart:update', (event) => {
    if (event.detail?.data?.didError) return;
    if (event.detail?.data?.source !== 'product-form-component') return;
    if (typeof window.refreshCartAndOpen === 'function') {
      window.refreshCartAndOpen();
    }
  });
}

/* --------------------------------------------------------------------------
   Sticky add-to-cart bar
   -------------------------------------------------------------------------- */
function initStickyAtc(bar) {
  const triggerSelector = (bar.dataset.rshTrigger || '').trim();
  const hideAtSelector = (bar.dataset.rshHideAt || '').trim();
  const trigger = triggerSelector ? document.querySelector(triggerSelector) : null;
  const hideAt = hideAtSelector ? document.querySelector(hideAtSelector) : null;

  const setVisible = (visible) => bar.classList.toggle('rsh-visible', visible);

  if (!trigger) {
    setVisible(false);
    return;
  }

  onScrollTick(() => {
    const scrolledPast = trigger.getBoundingClientRect().bottom < 0;
    const atEnd = hideAt ? hideAt.getBoundingClientRect().top < window.innerHeight - 200 : false;
    setVisible(scrolledPast && !atEnd);
  });
}

/* --------------------------------------------------------------------------
   Boot
   -------------------------------------------------------------------------- */
function boot() {
  if (!document.querySelector('.rsh-scope')) return;

  initLenis();
  initReveals();
  once(document.documentElement, 'rshCartBridgeReady', initCartDrawerBridge);

  document.querySelectorAll('[data-rsh-hero]').forEach((el) => once(el, 'rshHeroReady', initHero));
  document.querySelectorAll('[data-rsh-spotlight]').forEach((el) =>
    once(el, 'rshSpotlightReady', (root) => {
      initGallery(root);
      initDescription(root);
    })
  );
  document.querySelectorAll('[data-rsh-sticky]').forEach((el) =>
    once(el, 'rshStickyReady', initStickyAtc)
  );

  document.querySelectorAll('.rsh-scent').forEach((el) =>
    once(el, 'rshScentReady', (root) => initSnapDots(root, '.rsh-pyramid', '.rsh-pyramid-dots', 12))
  );
  document.querySelectorAll('.rsh-testimonials').forEach((el) =>
    once(el, 'rshReviewsReady', (root) => initSnapDots(root, '.rsh-testi-grid', '.rsh-testi-dots', 12))
  );
}

// Anchor handling is document-level, so it only needs binding once.
initAnchors();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}

// The theme editor swaps section markup in place; wire up the new nodes.
document.addEventListener('shopify:section:load', boot);
